import Logo from "@/app/icons/Logo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LogOut, Moon, Settings, Sun } from "lucide-react";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { NotificationPopover } from "./NotificationPopover";
import { MobileDrawer } from "./SidebarContent";
import { OptionMenu } from "@/components/app/OptionMenu";
import { ROUTES } from "@/lib/routes";
import type { Theme } from "../types";
import { useTheme } from "../useTheme";

const DashboardHeader = () => {
  const [theme, setTheme] = useState<Theme>("light");
  useTheme();

  // Initialize theme state from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as Theme | null;
    setTheme(savedTheme || "light");
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    
    if (newTheme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  };

  return (
    <header className="h-auto md:h-[90px] border-b md:border border-border-light px-5 py-3 flex items-center justify-between shrink-0 bg-background md:rounded-lg">
      <Link to={ROUTES.ROOT} className="block md:hidden">
        <Logo isCollapsed={false} />
      </Link>

      {/* Mobile hamburger — hidden on xl+ */}
      <div className="flex justify-end items-center gap-3">
        <div className="md:hidden  pr-3 border-r-2 border-[#887771]/50">
          <NotificationPopover />
        </div>
        <div className="">
          <MobileDrawer />
        </div>
      </div>

      {/* Right: bell + avatar menu */}
      <div className="hidden md:flex items-center gap-3 ml-auto">
        <div className="pr-3 border-r-2 border-[#887771]/50">
          <NotificationPopover />
        </div>

        <OptionMenu
          trigger={
            <button className="flex items-center gap-2 p-2.5 bg-gray-10/80 rounded-full transition-colors text-base font-medium hover:bg-gray-10">
              <Avatar className="h-8 w-8">
                <AvatarImage src="https://github.com/shadcn.png" alt="Admin" />
                <AvatarFallback>SA</AvatarFallback>
              </Avatar>
              <span>Super Admin</span>
            </button>
          }
          align="end"
          items={[
            {
              label: "Profile Settings",
              icon: <Settings size={14} />,
              onClick: () => window.location.href = ROUTES.DASHBOARD.ACCOUNT_SETTINGS,
            },
            {
              label: theme === "light" ? "Dark Mode" : "Light Mode",
              icon: theme === "light" ? <Moon size={14} /> : <Sun size={14} />,
              onClick: toggleTheme,
            },
            {
              label: "Logout",
              icon: <LogOut size={14} />,
              onClick: () => window.location.href = ROUTES.AUTH.LOGIN,
              destructive: true,
            },
          ]}
        />
      </div>
    </header>
  );
};

export default DashboardHeader;
